import parseUrl from 'url-parse';
import { getWebUrlParams } from '../../selectors/homeSelectors';
import { isFeatureActive } from '../../selectors/configSelectors';
import { GR_SEO_IMPROVEMENTS } from '../../flags/features';
import { languageForLocale } from '../locale';
import {
  errorForConfig,
  HomeAPIError,
  HomeAPI4xx,
  HomeAPI5xx,
  HomeAPITimeout,
  HomeAPIQueryError,
} from '../errors';

export const formatHomeError = (errorData) => {
  const { status, message = '', data = [] } = errorData;
  let error;
  let homeStatus = 200;

  // The standard for GraphQL is to error with a 200 status and return the error data in block instead.
  // It's possible that the query could fail even though it has a 200 status code and we'd want to report this.
  // This is why we also check the data block.
  if ([422, 200].includes(status) && data.length) {
    error = new HomeAPIQueryError(
      `API Error: Home (422) ${data.map((err) => `${err.message}`).join(', ')}`,
    );
    homeStatus = 422;
  } else if ([404, 400].includes(status)) {
    homeStatus = 404;
  } else if (Math.floor(status / 100) === 4) {
    error = new HomeAPI4xx(`API Error: Home (${status}) ${message}`);
    homeStatus = 500;
  } else if (status === 504) {
    error = new HomeAPITimeout(`API Error: Home (${status}) ${message}`);
    homeStatus = 500;
  } else if (Math.floor(status / 100) === 5) {
    error = new HomeAPI5xx(`API Error: Home (${status}) ${message}`);
    homeStatus = 500;
  } else {
    error = new HomeAPIError(`API Error: Home (${status}) ${message}`);
    homeStatus = 500;
  }

  return { error, status: homeStatus };
};

export const statusForState = ({ config, home }) => {
  let error;
  let status = 200;
  let isConfigError;
  const { feed } = home;
  const configError = errorForConfig(config);

  if (configError) {
    status = 500;
    error = configError;
    isConfigError = true;
  }

  if (!error && feed.error) {
    const homeError = formatHomeError(feed.error);
    status = homeError.status;
    error = homeError.error;
  }

  return {
    status,
    error,
    isConfigError,
  };
};

export const buildCanonicalUrl = (requestPath, state) => {
  if (state.home.feed.error) {
    return requestPath;
  }

  const request = parseUrl(state.request.host + requestPath);
  const response = parseUrl(getWebUrlParams(state));
  const isDevEnv = /\.dev$/.test(request.hostname);

  const canonicalHostname = isDevEnv
    ? `${response.hostname.replace('test.', '')}.dev`
    : response.hostname;

  const localePath = request.pathname.split('/restaurants')[0];
  const cityNeighbourhoodPath = response.pathname.split('/restaurants')[1];
  // Build canonical path name and remove any trailing slashes
  const canonicalPathname = `${localePath}/restaurants${cityNeighbourhoodPath}`.replace(/\/$/, '');

  return canonicalHostname === request.hostname
    ? canonicalPathname + request.query
    : `https://${canonicalHostname}${canonicalPathname}${request.query}`;
};

// 'locale', 'neighborhood', 'city' are returned in request.currentQuery, we want to ignore these.
// Any additional params in the object will come from a query string.
export const hasQueryStringParams = (currentQuery = {}) => {
  const WHITELIST_PARAMS = ['locale', 'neighborhood', 'city'];
  const queryParams = Object.keys(currentQuery).filter(
    (param) => !WHITELIST_PARAMS.includes(param),
  );

  return Boolean(queryParams.length);
};

export const getUrlWithoutLocale = ({ currentUrl, locale }) => currentUrl.replace(`/${locale}`, '');

export const getAlternateLocales = (
  { country, host, currentUrl, currentQuery, locale },
  config,
) => {
  const urlWithoutLocale = getUrlWithoutLocale({ currentUrl, locale });
  // we want Google to ignore the page when query string paramters are present, e.g. ?postcode=
  // in this scenario, listing no alternate locales will help to achieve this
  const isSeoImprovementEnabled = isFeatureActive({ config }, GR_SEO_IMPROVEMENTS);
  const shouldNotListAlternateLocales =
    isSeoImprovementEnabled && hasQueryStringParams(currentQuery);

  return shouldNotListAlternateLocales
    ? []
    : country.languages.map((language) => {
        const langLocale = language.toLowerCase();

        return {
          href: `${host}/${langLocale}${urlWithoutLocale}`,
          hrefLang: languageForLocale(language, country.isoCode),
        };
      });
};
